import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const { 
  FiFileText, FiCalendar, FiUser, FiMessageCircle, FiBookmark,
  FiShare2, FiSearch, FiFilter, FiTrendingUp, FiEye, FiHeart,
  FiClock, FiTag, FiMoreHorizontal, FiEdit3, FiTrash2
} = FiIcons;

const BlogPortal = () => {
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'all', label: 'All Articles', count: 24 },
    { id: 'clinical', label: 'Clinical Insights', count: 8 },
    { id: 'technology', label: 'Technology', count: 6 },
    { id: 'practice', label: 'Practice Management', count: 5 },
    { id: 'industry', label: 'Industry News', count: 5 }
  ];

  const articles = [
    {
      id: 1,
      title: 'The Future of Digital Dentistry: Trends to Watch in 2024',
      excerpt: 'As we move further into the digital age, dentistry continues to evolve at an unprecedented pace. From AI-powered diagnostics to advanced 3D printing technologies, the landscape of dental care is being transformed...',
      content: 'Full article content here...',
      author: {
        name: 'Dr. Sarah Mitchell',
        avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=40&h=40&fit=crop&crop=face',
        role: 'Oral Surgeon',
        bio: '15+ years experience in digital dentistry and implantology'
      },
      category: 'technology',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop',
      publishedDate: '2024-01-15',
      readTime: '8 min read',
      views: 1250,
      likes: 89,
      comments: 23,
      featured: true,
      tags: ['digital-dentistry', 'AI', '3D-printing', 'innovation']
    },
    {
      id: 2,
      title: 'Patient Communication Strategies for Treatment Acceptance',
      excerpt: 'Effective communication is the cornerstone of successful dental practice management. Learn proven strategies to improve treatment plan acceptance rates and build stronger patient relationships...',
      content: 'Full article content here...',
      author: {
        name: 'Dr. Michael Chen',
        avatar: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=40&h=40&fit=crop&crop=face',
        role: 'General Dentist',
        bio: 'Practice management consultant and dental practice owner'
      },
      category: 'practice',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop',
      publishedDate: '2024-01-14',
      readTime: '6 min read',
      views: 892,
      likes: 67,
      comments: 18,
      featured: false,
      tags: ['patient-care', 'communication', 'practice-growth']
    },
    {
      id: 3,
      title: 'Breaking: New Guidelines for Antibiotic Prophylaxis Released',
      excerpt: 'The American Dental Association has released updated guidelines for antibiotic prophylaxis in dental procedures. Key changes include revised criteria for at-risk patients and new recommendations for specific procedures...',
      content: 'Full article content here...',
      author: {
        name: 'DentalBridge Editorial Team',
        avatar: 'https://ui-avatars.com/api/?name=DentalBridge&background=3b82f6&color=fff',
        role: 'Industry News',
        bio: 'Curating the latest dental industry news and updates'
      },
      category: 'industry',
      image: 'https://images.unsplash.com/photo-1582719471384-894fba16d114?w=600&h=400&fit=crop',
      publishedDate: '2024-01-13',
      readTime: '4 min read',
      views: 2156,
      likes: 124,
      comments: 45,
      featured: true,
      tags: ['guidelines', 'antibiotics', 'ADA', 'updates']
    },
    {
      id: 4,
      title: 'Minimally Invasive Techniques in Modern Periodontics',
      excerpt: 'Explore the latest advancements in minimally invasive periodontal treatments that are revolutionizing patient care. From laser therapy to innovative surgical approaches...',
      content: 'Full article content here...',
      author: {
        name: 'Dr. Lisa Rodriguez',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face',
        role: 'Periodontist',
        bio: 'Specialist in minimally invasive periodontal treatments'
      },
      category: 'clinical',
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600&h=400&fit=crop',
      publishedDate: '2024-01-12',
      readTime: '10 min read',
      views: 756,
      likes: 58,
      comments: 12,
      featured: false,
      tags: ['periodontics', 'minimally-invasive', 'laser-therapy']
    }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      clinical: 'bg-blue-100 text-blue-800',
      technology: 'bg-purple-100 text-purple-800',
      practice: 'bg-green-100 text-green-800',
      industry: 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = activeCategory === 'all' || article.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredArticles = filteredArticles.filter(article => article.featured);
  const regularArticles = filteredArticles.filter(article => !article.featured);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dental Insights & News</h1>
              <p className="text-gray-600 mt-1">Stay updated with the latest trends, research, and industry news.</p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiBookmark} className="w-4 h-4" />
                <span>Saved Articles</span>
              </button>
              {(user.role === 'admin' || user.role === 'provider') && (
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                  <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                  <span>Write Article</span>
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Search and Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                />
              </div>
              <button className="p-2 text-gray-400 hover:text-gray-600">
                <SafeIcon icon={FiFilter} className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center space-x-2 overflow-x-auto">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap
                    ${activeCategory === category.id
                      ? 'bg-blue-50 text-blue-600 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  {category.label}
                  <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                    {category.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Featured Articles */}
        {featuredArticles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Articles</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featuredArticles.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all group cursor-pointer"
                >
                  <div className="relative">
                    <img 
                      src={article.image} 
                      alt={article.title} 
                      className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getCategoryColor(article.category)}`}>
                        {article.category}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                        Featured
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{article.excerpt}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <img
                          src={article.author.avatar}
                          alt={article.author.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{article.author.name}</p>
                          <p className="text-xs text-gray-500">{new Date(article.publishedDate).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiClock} className="w-4 h-4" />
                          <span>{article.readTime}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                          <span>{article.views}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Regular Articles Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Latest Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularArticles.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all group cursor-pointer"
              >
                <div className="relative">
                  <img 
                    src={article.image} 
                    alt={article.title} 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getCategoryColor(article.category)}`}>
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3 text-sm">{article.excerpt}</p>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <img
                        src={article.author.avatar}
                        alt={article.author.name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-gray-600">{article.author.name}</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-500">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiClock} className="w-3 h-3" />
                        <span>{article.readTime}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiEye} className="w-3 h-3" />
                        <span>{article.views}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <button className="flex items-center space-x-1 hover:text-blue-600 transition-colors">
                        <SafeIcon icon={FiHeart} className="w-4 h-4" />
                        <span>{article.likes}</span>
                      </button>
                      <button className="flex items-center space-x-1 hover:text-blue-600 transition-colors">
                        <SafeIcon icon={FiMessageCircle} className="w-4 h-4" />
                        <span>{article.comments}</span>
                      </button>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600">
                      <SafeIcon icon={FiBookmark} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>

        {/* Newsletter Signup */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-8 text-white"
        >
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
            <p className="text-blue-100 mb-6">Get the latest dental insights delivered to your inbox weekly.</p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button className="px-6 py-3 bg-white text-blue-600 rounded-lg font-medium hover:bg-gray-50 transition-colors">
                Subscribe
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPortal;